import React, {useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {useSearchParams} from 'react-router-dom'
import {AzPageHeader} from '../../../components/AzPageHeader'
import {KTCard, KTCardBody} from '../../../../_dashboard/helpers'
import CitiesTab from './tabs/CitiesTab'
import CurrencyTab from './tabs/CurrencyTab'
import PersonTypesTab from './tabs/PersonTypesTab'
import CategoriesTab from './tabs/CategoriesTab'
import TourTypesTab from './tabs/TourTypesTab'
import FeaturedConfigTab from './tabs/FeaturedConfigTab'
import PaymentMethodsTab from './tabs/PaymentMethodsTab'
import GoogleTagConfigTab from './tabs/GoogleTagConfigTab'

type ConfigTabId =
  | 'cities'
  | 'categories'
  | 'tourTypes'
  | 'personTypes'
  | 'currency'
  | 'paymentMethods'
  | 'featured'
  | 'googleTag'

const VALID_TABS: ConfigTabId[] = [
  'cities',
  'categories',
  'tourTypes',
  'personTypes',
  'currency',
  'paymentMethods',
  'featured',
  'googleTag',
]

type ConfigTabItem = {
  id: ConfigTabId
  title: string
  icon: string
  section: 'catalog' | 'business'
}

const ConfigurationsPage: React.FC = () => {
  const intl = useIntl()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabFromUrl = searchParams.get('tab') as ConfigTabId | null
  const initialTab =
    tabFromUrl && VALID_TABS.includes(tabFromUrl) ? tabFromUrl : 'cities'
  const [activeTab, setActiveTab] = useState<ConfigTabId>(initialTab)

  useEffect(() => {
    if (tabFromUrl && VALID_TABS.includes(tabFromUrl) && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl)
    }
  }, [tabFromUrl])

  useEffect(() => {
    if (!tabFromUrl) {
      setSearchParams({tab: initialTab}, {replace: true})
    }
  }, [])

  const selectTab = (tabId: ConfigTabId) => {
    setActiveTab(tabId)
    setSearchParams({tab: tabId}, {replace: true})
  }

  const tabs: ConfigTabItem[] = [
    {
      id: 'cities',
      title: intl.formatMessage({id: 'TOUR_CONFIGURATIONS.CITIES'}),
      icon: 'location_city',
      section: 'catalog',
    },
    {
      id: 'categories',
      title: intl.formatMessage({id: 'TOUR_CONFIGURATIONS.CATEGORIES'}),
      icon: 'category',
      section: 'catalog',
    },
    {
      id: 'tourTypes',
      title: intl.formatMessage({id: 'TOUR.FIELDS.TYPE'}),
      icon: 'tour',
      section: 'catalog',
    },
    {
      id: 'personTypes',
      title: intl.formatMessage({id: 'TOUR_CONFIGURATIONS.PERSON_TYPES'}),
      icon: 'groups',
      section: 'catalog',
    },
    {
      id: 'currency',
      title: intl.formatMessage({id: 'TOUR_CONFIGURATIONS.CURRENCY'}),
      icon: 'currency_exchange',
      section: 'business',
    },
    {
      id: 'paymentMethods',
      title: intl.formatMessage({id: 'TOUR_CONFIGURATIONS.PAYMENT_METHODS'}),
      icon: 'payments',
      section: 'business',
    },
    {
      id: 'featured',
      title: intl.formatMessage({id: 'TOUR_CONFIGURATIONS.FEATURED'}),
      icon: 'star',
      section: 'business',
    },
    {
      id: 'googleTag',
      title: intl.formatMessage({id: 'TOUR_CONFIGURATIONS.GOOGLE_TAG'}),
      icon: 'analytics',
      section: 'business',
    },
  ]

  const renderTabContent = () => {
    switch (activeTab) {
      case 'cities':
        return <CitiesTab />
      case 'categories':
        return <CategoriesTab />
      case 'tourTypes':
        return <TourTypesTab />
      case 'personTypes':
        return <PersonTypesTab />
      case 'currency':
        return <CurrencyTab />
      case 'featured':
        return <FeaturedConfigTab />
      case 'googleTag':
        return <GoogleTagConfigTab />
      case 'paymentMethods':
        return <PaymentMethodsTab />
      default:
        return null
    }
  }

  const tabStyle = (isActive: boolean) => ({
    cursor: 'pointer',
    color: isActive ? '#0f172a' : '#475569',
    backgroundColor: isActive ? '#eef4ff' : 'transparent',
    borderLeft: isActive ? '3px solid #3b82f6' : '3px solid transparent',
    transition: 'all 0.2s ease',
    padding: '0.8rem 1rem',
    marginBottom: '0.25rem',
    fontWeight: isActive ? '600' : '500',
    borderRadius: '0.65rem',
  })

  const renderNavSection = (section: ConfigTabItem['section'], label: string) => (
    <div className='mb-5'>
      <div className='text-uppercase text-gray-500 fs-8 fw-bold px-3 mb-2'>{label}</div>
      {tabs
        .filter((tab) => tab.section === section)
        .map((tab) => {
          const isActive = activeTab === tab.id
          return (
            <div
              key={tab.id}
              style={tabStyle(isActive)}
              onClick={() => selectTab(tab.id)}
              role='button'
            >
              <div className='d-flex align-items-center gap-3'>
                <span
                  className='material-symbols-outlined'
                  style={{
                    fontSize: '18px',
                    color: isActive ? '#2563eb' : '#64748b',
                    fontVariationSettings: isActive
                      ? "'FILL' 1, 'wght' 500, 'GRAD' 0, 'opsz' 24"
                      : "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24",
                  }}
                >
                  {tab.icon}
                </span>
                <span>{tab.title}</span>
              </div>
            </div>
          )
        })}
    </div>
  )

  return (
    <>
      <AzPageHeader title={intl.formatMessage({id: 'TOUR_CONFIGURATIONS.TITLE'})} />

      <KTCard>
        <KTCardBody>
          <div className='bo-config-layout'>
            <div className='flex-column flex-lg-row-auto w-100 w-lg-250px'>
              <div className='nav flex-column nav-tabs border-0 border-end h-lg-100 pe-lg-3 bo-config-nav'>
                {renderNavSection('catalog', 'Configuration de base')}
                {renderNavSection('business', 'Business & affichage')}
              </div>
            </div>

            <div className='flex-lg-row-fluid ms-lg-7'>{renderTabContent()}</div>
          </div>
        </KTCardBody>
      </KTCard>
    </>
  )
}

export default ConfigurationsPage
